/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeEvent, FocusEvent, ReactElement } from 'react';
import { Field, FieldProps, FieldRenderProps } from 'react-final-form';

import { Textarea, ITextareaProps } from './Textarea';

type Value = ITextareaProps['value'];
type ITextareaFieldProps = ITextareaProps &
  FieldProps<Value, FieldRenderProps<Value>> & {
    fieldKey?: ReactElement['key'];
  };

export const TextareaField = (props: ITextareaFieldProps) => {
  const {
    afterSubmit,
    allowNull,
    beforeSubmit,
    data,
    defaultValue,
    format,
    formatOnBlur,
    initialValue,
    isEqual,
    multiple,
    name,
    parse,
    subscription,
    type,
    validate,
    validateFields,
    value,
    fieldKey,
    ...componentProps
  } = props;

  return (
    <Field
      key={fieldKey} // Required for re-render validation. Cause an error: "Cannot update a component (`ForwardRef(Field)`) while rendering a different component (`ForwardRef(Field)`)." https://github.com/final-form/react-final-form/issues/850
      fieldKey={fieldKey}
      afterSubmit={afterSubmit}
      allowNull={allowNull}
      beforeSubmit={beforeSubmit}
      data={data}
      defaultValue={defaultValue}
      format={format}
      formatOnBlur={formatOnBlur}
      initialValue={initialValue}
      isEqual={isEqual}
      multiple={multiple}
      name={name}
      parse={parse}
      subscription={subscription}
      type={type}
      validate={validate}
      validateFields={validateFields}
      value={value}
    >
      {({ input, meta }) => {
        const { onBlur, onChange, onFocus, ...restComponentProps } = componentProps;

        function handleBlur(e: FocusEvent<HTMLTextAreaElement>) {
          input.onBlur(e);
          onBlur?.(e);
        }

        function handleChange(e: ChangeEvent<HTMLTextAreaElement>) {
          input.onChange(e);
          onChange?.(e);
        }

        function handleFocus(e: FocusEvent<HTMLTextAreaElement>) {
          input.onFocus(e);
          onFocus?.(e);
        }

        const error =
          (meta.touched && restComponentProps.error) || (meta.touched && meta.error) ? meta.error : undefined;

        return (
          <Textarea
            {...restComponentProps}
            {...input}
            error={error}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
          />
        );
      }}
    </Field>
  );
};
